import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { ImportAiClassificationExampleModal } from '@client/components/admin/ai-classification-example/ImportAiClassificationExampleModal';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Empty, Tag, Tooltip } from 'antd';
import { Download, Library, Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { ClassificationExampleFilters } from './ClassificationExampleFilters';

const EMPTY_AVAILABLE_TAGS: string[] = [];

export const Route = createFileRoute('/admin-console/platform-content/ai-training/classification-example')({
    staticData: {
        breadcrumb: {
            title: 'Classification examples',
        },
        ui: {
            icon: Library,
            title: 'Classification examples management',
            subtitle: 'Curated email examples for complaint disambiguation',
            menuDescription: 'Manage classification training examples',
        },
    },
    component: ClassificationExamples,
});

type QueryParams = RouterInputs['admin']['aiClassificationExample']['listAiClassificationExample'];
type ExampleData = RouterOutputs['admin']['aiClassificationExample']['listAiClassificationExample'];
type ExampleRow = NonNullable<ExampleData>['rows'][number];
type OrderBy = NonNullable<QueryParams['orderBy']>;

function renderExpectedAxis(value: boolean | null, trueLabel: string): ReactNode {
    if (value === null) {
        return (
            <Tooltip title="Derived from tags at prompt render time">
                <Tag variant="outlined" className="text-xs">
                    Derive
                </Tag>
            </Tooltip>
        );
    }

    return (
        <Tag variant="outlined" color={value ? 'green' : 'default'} className="text-xs">
            {value ? trueLabel : 'No'}
        </Tag>
    );
}

function ClassificationExamples() {
    const { trpc } = useTrpc();
    const navigate = useNavigate();
    const { formatDate } = useFormatter();
    const { isReadOnly } = useAdminModulePermissions('ai');
    const columnHelper = createColumnHelper<ExampleRow, OrderBy>();
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [queryParams, setQueryParams] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });

    const { data, isLoading, refetch, error } =
        trpc.admin.aiClassificationExample.listAiClassificationExample.useQuery(queryParams);
    const availableTags = data?.availableTags ?? EMPTY_AVAILABLE_TAGS;

    const { mutate: deleteExample } = trpc.admin.aiClassificationExample.deleteAiClassificationExample.useMutation({
        onSuccess: () => {
            message.success('Example deleted successfully');
            void refetch();
        },
        onError: (err) => {
            errorMessage.show(err);
        },
    });

    const columns = [
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            sortable: 'name',
            cell: ({ value }) => (
                <div className="font-medium">
                    {value || <span className="text-subtle-foreground italic">Unnamed</span>}
                </div>
            ),
            width: 220,
        }),
        columnHelper.accessor('emailExample', {
            id: 'emailExample',
            header: 'Email body',
            cell: ({ value }) => (
                <div className="max-w-[420px] truncate text-subtle-foreground" title={value}>
                    {value}
                </div>
            ),
            width: 420,
        }),
        columnHelper.accessor('tags', {
            id: 'tags',
            header: 'Tags',
            cell: ({ value }) => {
                if (!value || value.length === 0) {
                    return <span className="text-sm text-subtle-foreground">No tags</span>;
                }
                return (
                    <Space size={4} wrap>
                        {value.slice(0, 3).map((tag: string) => (
                            <Tag variant="outlined" key={tag} color="geekblue" className="text-xs">
                                {tag}
                            </Tag>
                        ))}
                        {value.length > 3 && (
                            <Tooltip title={value.slice(3).join(', ')}>
                                <Tag variant="outlined" className="cursor-help text-xs">
                                    +{value.length - 3}
                                </Tag>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
            width: 220,
        }),
        columnHelper.display({
            id: 'expectedAxes',
            header: 'Expected',
            cell: ({ row }) => (
                <Space size={4} wrap>
                    {renderExpectedAxis(row.expectedIsIncident, 'Incident')}
                    {renderExpectedAxis(row.expectedIsComplaint, 'Complaint')}
                </Space>
            ),
            width: 180,
        }),
        columnHelper.display({
            id: 'masterObligation',
            header: 'Obligation',
            cell: ({ row }) => {
                if (!row.masterObligation) {
                    return <span className="text-sm text-subtle-foreground">—</span>;
                }
                return (
                    <Tag variant="outlined" color="blue" className="font-mono text-xs">
                        {row.masterObligation.identificationCode ?? `id ${row.masterObligation.id}`}
                    </Tag>
                );
            },
            width: 140,
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'Created',
            sortable: 'createdAt',
            cell: ({ value }) => formatDate(value),
            width: 120,
        }),
        columnHelper.accessor('updatedAt', {
            id: 'updatedAt',
            header: 'Updated',
            sortable: 'updatedAt',
            cell: ({ value }) => formatDate(value),
            width: 120,
        }),
        columnHelper.actions({
            id: 'actions',
            cell: ({ row }) => (
                <Space size="small">
                    <Button.View.Link
                        to="/admin-console/platform-content/ai-training/classification-example/view/$id"
                        params={{ id: row.id }}
                    />
                    {!isReadOnly && (
                        <>
                            <Button.Edit.Link
                                to="/admin-console/platform-content/ai-training/classification-example/edit/$id"
                                params={{ id: row.id }}
                            />
                            <Button.Delete
                                confirmTitle="Delete classification example"
                                confirmDescription="Are you sure you want to delete this example? This action cannot be undone."
                                onConfirm={(onSuccess) => {
                                    deleteExample(
                                        { id: row.id },
                                        {
                                            onSuccess: () => onSuccess(),
                                        },
                                    );
                                }}
                            />
                        </>
                    )}
                </Space>
            ),
            fixed: 'right',
            align: 'center',
        }),
    ];

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        queryParams: queryParams,
        onParamsChange: (params) => {
            setQueryParams(params);
        },
        pagination: {
            position: 'both',
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'classification-example-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        showSorterTooltip: false,
    });

    const handleFilterChange = (where: Partial<QueryParams['where']>) => {
        setQueryParams((prev) => ({ ...prev, where, offset: 0 }));
    };

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary error={error}>
                <ReadOnlyBanner module="ai" />
                <TableToolbar
                    filters={
                        <ClassificationExampleFilters availableTags={availableTags} onChange={handleFilterChange} />
                    }
                    variant="enhanced"
                    actions={
                        !isReadOnly ? (
                            <Space>
                                <Button type="secondary" icon={<Download />} onClick={() => setImportModalOpen(true)}>
                                    Import CSV
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<Plus />}
                                    onClick={() =>
                                        navigate({
                                            to: '/admin-console/platform-content/ai-training/classification-example/create',
                                        })
                                    }
                                >
                                    Add example
                                </Button>
                            </Space>
                        ) : undefined
                    }
                />

                {data?.rows.length === 0 && !isLoading ? (
                    <Empty
                        description={
                            <Space orientation="vertical" size="small">
                                <span>No classification examples found</span>
                                <span className="text-sm text-subtle-foreground">
                                    {queryParams.where
                                        ? 'Try adjusting your filters or search criteria'
                                        : 'Create your first classification example to get started'}
                                </span>
                            </Space>
                        }
                        className="py-12"
                    >
                        {!queryParams.where && (
                            <Button
                                type="primary"
                                icon={<Plus />}
                                onClick={() =>
                                    navigate({
                                        to: '/admin-console/platform-content/ai-training/classification-example/create',
                                    })
                                }
                                disabled={isReadOnly}
                            >
                                Create example
                            </Button>
                        )}
                    </Empty>
                ) : (
                    <PrismaTable {...tableProps} />
                )}

                <ImportAiClassificationExampleModal
                    open={importModalOpen}
                    onClose={() => setImportModalOpen(false)}
                    onSuccess={() => {
                        void refetch();
                    }}
                />

                {/* Drawer overlays for create/edit/view live on dedicated child routes. */}
                <Outlet />
            </TRPCErrorBoundary>
        </PageController>
    );
}
